import { useState, useEffect } from 'react';
import { FileText, Download, Filter } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageSEO from '../components/PageSEO';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { value: 'all', label: 'Tous' },
  { value: 'etat_civil', label: 'État Civil' },
  { value: 'urbanisme', label: 'Urbanisme' },
  { value: 'fiscalite', label: 'Fiscalité' },
  { value: 'deliberations', label: 'Délibérations' },
  { value: 'education', label: 'Éducation' },
  { value: 'sante', label: 'Santé' },
];

const CATEGORY_LABELS = Object.fromEntries(CATEGORIES.map(c => [c.value, c.label]));

const getFileUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

const DocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/documents`)
      .then(res => setDocuments(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getDocsByCategory = (cat) =>
    cat === 'all' ? documents : documents.filter(d => d.category === cat);

  const activeCats = CATEGORIES.filter(
    c => c.value === 'all' || documents.some(d => d.category === c.value)
  );

  return (
    <main className="pt-14 md:pt-16" data-testid="documents-page">
      <PageSEO
        title="Documents officiels"
        description="Téléchargez les documents administratifs officiels de la commune de Gandiaye : état civil, urbanisme, fiscalité, délibérations."
        path="/documents"
      />

      <section className="bg-emerald-700 py-12 md:py-16">
        <div className="container-main">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-2">
            Documents Officiels
          </h1>
          <p className="text-white/80 text-lg max-w-2xl">
            Retrouvez les documents administratifs publiés par la mairie de Gandiaye.
          </p>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="container-main">
          {loading ? (
            <div className="flex justify-center py-16"><div className="loader"></div></div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
              <p className="text-neutral-500 text-lg">Aucun document publié pour le moment.</p>
            </div>
          ) : (
            <Tabs defaultValue="all" className="w-full">
              <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-neutral-100 p-2 rounded-xl mb-8">
                {activeCats.map(cat => (
                  <TabsTrigger key={cat.value} value={cat.value} className="flex-1 md:flex-none" data-testid={`tab-doc-${cat.value}`}>
                    {cat.label}
                    {cat.value !== 'all' && (
                      <span className="ml-1.5 text-xs bg-white/60 px-1.5 py-0.5 rounded-full">
                        {getDocsByCategory(cat.value).length}
                      </span>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>

              {activeCats.map(cat => (
                <TabsContent key={cat.value} value={cat.value}>
                  <div className="space-y-3">
                    {getDocsByCategory(cat.value).map(doc => (
                      <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`doc-${doc.id}`}>
                        <CardContent className="p-5 flex items-center justify-between gap-4">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-neutral-800 truncate">{doc.title}</h3>
                              <div className="flex items-center gap-3 text-sm text-neutral-500 mt-0.5">
                                <span className="bg-neutral-100 px-2 py-0.5 rounded text-xs font-medium">
                                  {CATEGORY_LABELS[doc.category] || doc.category}
                                </span>
                                {doc.description && <span className="truncate">{doc.description}</span>}
                              </div>
                            </div>
                          </div>
                          <a
                            href={getFileUrl(doc.file_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex-shrink-0"
                            data-testid={`download-${doc.id}`}
                          >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Télécharger</span>
                          </a>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>
    </main>
  );
};

export default DocumentsPage;
